// Permission forwarding: a site in the cloud browser asks for a capability → the user
// decides in our UI → we request the *same* capability from the real local browser
// and hand the result (media track, coordinates, clipboard text) to the server.
import type { Api } from './api';
import { platform } from './platform';
import type { MediaKind, PermissionKind } from './protocol';
import type { RemoteSession } from './session';

export interface PermissionRequest {
  id: number;
  origin: string;
  kinds: PermissionKind[];
  tabId: string;
}

export interface AcquireResult {
  ok: boolean;
  text?: string;
  geo?: { lat: number; lon: number; acc: number };
  error?: string;
}

/** Kinds that are never auto-granted from a remembered decision (need a fresh user gesture). */
const NEVER_REMEMBER: PermissionKind[] = ['display-capture'];

export class PermissionBroker {
  private remembered = new Map<string, boolean>();
  private geoWatch?: number;

  constructor(private api: Api, private session: () => RemoteSession | undefined, private onMediaChange: () => void) {}

  async load() {
    try {
      const list = await this.api.permissions();
      this.remembered.clear();
      for (const p of list) this.remembered.set(`${p.origin}|${p.kind}`, p.allow);
      return list;
    } catch {
      return [];
    }
  }

  list() {
    return [...this.remembered.entries()].map(([k, allow]) => {
      const [origin, kind] = k.split('|');
      return { origin, kind, allow };
    });
  }

  /** 'allow' / 'deny' if all requested kinds have a remembered decision, else null. */
  remembered_(req: PermissionRequest): 'allow' | 'deny' | null {
    let all = true;
    for (const k of req.kinds) {
      const v = this.remembered.get(`${req.origin}|${k}`);
      if (v === false) return 'deny';
      if (v !== true || NEVER_REMEMBER.includes(k)) all = false;
    }
    return all ? 'allow' : null;
  }

  async forget(origin: string, kind: string) {
    this.remembered.delete(`${origin}|${kind}`);
    await this.api.deletePermission(origin, kind).catch(() => {});
    this.session()?.send({ t: 'resetPermission', origin, kind });
  }

  /** Apply the user's decision. Must be called from a user gesture when allow=true. */
  async decide(req: PermissionRequest, allow: boolean, remember: boolean): Promise<AcquireResult> {
    const s = this.session();
    let res: AcquireResult = { ok: false };
    if (allow) res = await this.acquire(req.kinds);
    const granted = allow && res.ok;
    s?.send({ t: 'permissionResponse', id: req.id, allow: granted, text: res.text, geo: res.geo });
    // Remember explicit blocks, and grants that actually worked locally.
    if (remember && (granted || !allow)) {
      for (const k of req.kinds) {
        if (NEVER_REMEMBER.includes(k)) continue;
        this.remembered.set(`${req.origin}|${k}`, granted);
        this.api.setPermission(req.origin, k, granted).catch(() => {});
      }
    }
    return res;
  }

  async acquire(kinds: PermissionKind[]): Promise<AcquireResult> {
    const s = this.session();
    if (!s) return { ok: false };
    try {
      const wantCam = kinds.includes('camera') && !s.hasLocalTrack('camera');
      const wantMic = kinds.includes('microphone') && !s.hasLocalTrack('microphone');
      if (wantCam || wantMic) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: wantCam ? { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } } : false,
          audio: wantMic ? { echoCancellation: true, noiseSuppression: true, autoGainControl: true } : false,
        });
        for (const tr of stream.getTracks()) {
          const kind: MediaKind = tr.kind === 'video' ? 'camera' : 'microphone';
          tr.onended = () => this.stop(kind);
          await s.setLocalTrack(kind, tr);
        }
        this.onMediaChange();
      }
      if (kinds.includes('display-capture')) {
        const stream = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: { ideal: 24 } }, audio: false });
        const tr = stream.getVideoTracks()[0];
        tr.onended = () => this.stop('screen');
        await s.setLocalTrack('screen', tr);
        this.onMediaChange();
      }
      let geo: AcquireResult['geo'];
      if (kinds.includes('geolocation')) {
        geo = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(
            (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude, acc: p.coords.accuracy }),
            reject,
            { enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 },
          ),
        );
        this.watchGeo();
      }
      if (kinds.includes('notifications')) {
        if (!(await platform().requestNotificationPermission())) return { ok: false, error: 'notifications' };
      }
      let text: string | undefined;
      if (kinds.includes('clipboard-read')) {
        text = await navigator.clipboard.readText();
      }
      return { ok: true, geo, text };
    } catch (e: any) {
      console.warn('local permission request failed', e);
      return { ok: false, error: e?.name ?? String(e) };
    }
  }

  private watchGeo() {
    if (this.geoWatch !== undefined) return;
    this.geoWatch = navigator.geolocation.watchPosition(
      (p) => this.session()?.send({ t: 'geo', lat: p.coords.latitude, lon: p.coords.longitude, acc: p.coords.accuracy }),
      () => {},
      { enableHighAccuracy: true, maximumAge: 10_000 },
    );
  }

  stop(kind: MediaKind) {
    this.session()?.setLocalTrack(kind, null);
    this.onMediaChange();
  }

  dispose() {
    if (this.geoWatch !== undefined) navigator.geolocation.clearWatch(this.geoWatch);
  }
}
